/**
 * Initial-mark avatar for an entity — an insurer, a client, a user.
 *
 * Why it exists: a column of insurer names is read letter by letter, but a column of
 * marks is read by shape and colour, so "all the Interamerican rows" resolves in one
 * pass instead of eight. That is the entire justification, and it is why the mark sits
 * *beside* the name rather than replacing it — the avatar accelerates scanning, the text
 * still does the identifying.
 *
 * The tint is derived, not stored. `hashIndex` maps the name onto one of twelve hue
 * steps and the colour is built from that hue in OKLCH, which means: a given insurer is
 * always the same colour without a lookup table, adding an insurer needs no palette
 * edit, and the chroma is pinned low (0.03) so twelve distinct marks still read as one
 * restrained neutral family rather than a bag of sweets. Lightness is fixed per theme,
 * so contrast is constant across every hue instead of drifting the way a hand-picked
 * palette does.
 */

import { cn, companyInitials, hashIndex, initials } from '@/lib/utils';

const SIZES = {
  xs: 'size-5 text-[9px]',
  sm: 'size-6 text-[10px]',
  default: 'size-7 text-[11px]',
  lg: 'size-9 text-[13px]',
} as const;

export interface EntityAvatarProps {
  name: string | null | undefined;
  /** Decides how the two letters are derived — see `companyInitials`. */
  kind?: 'company' | 'person';
  size?: keyof typeof SIZES;
  className?: string;
}

export function EntityAvatar({ name, kind = 'company', size = 'default', className }: EntityAvatarProps) {
  const label = name?.trim() || '—';
  const mark = kind === 'company' ? companyInitials(label) : initials(label);
  const hue = hashIndex(label, 12) * 30;

  return (
    <span
      // aria-hidden: the name is always rendered next to this, so a screen reader that
      // also announced "E T" would be reading the same entity twice.
      aria-hidden
      className={cn(
        'entity-avatar inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
        'tracking-[0.01em] select-none',
        SIZES[size],
        className,
      )}
      style={{ '--avatar-hue': hue } as React.CSSProperties}
    >
      {mark}
    </span>
  );
}

/**
 * Avatar + name, the pairing used in every table cell that names an organisation.
 * Bundled so the gap, truncation and vertical alignment cannot drift between columns.
 */
export function EntityCell({
  name,
  kind = 'company',
  size = 'sm',
  muted = false,
  className,
}: EntityAvatarProps & { muted?: boolean }) {
  if (!name) return <span className="text-muted-soft">—</span>;

  return (
    <span className={cn('flex min-w-0 items-center gap-2', className)}>
      <EntityAvatar name={name} kind={kind} size={size} />
      <span className={cn('truncate', muted && 'text-muted-foreground')}>{name}</span>
    </span>
  );
}
